<template>
  <div class="flow-element-test-container">
    <h2>Test Flow "{{ flowObject.displayName }}" - {{ flowObject.category }}</h2>
    
    <div class="flow-controls">
      <div class="block-selector">
        <label for="blockSelect">Sélectionner un bloc :</label>
        <select 
          id="blockSelect" 
          v-model="selectedBlockId" 
          @change="loadSelectedBlock"
          class="block-select"
          :disabled="loading || loadingBlocks"
        >
          <option value="">-- Choisir un bloc --</option>
          <optgroup label="Flux Principaux" v-if="primaryBlocks.length > 0">
            <option 
              v-for="block in primaryBlocks" 
              :key="block._id" 
              :value="block._id"
            >
              {{ block.displayName }}
            </option>
          </optgroup>
          <optgroup label="Flux Standards" v-if="secondaryBlocks.length > 0">
            <option 
              v-for="block in secondaryBlocks" 
              :key="block._id" 
              :value="block._id"
            >
              {{ block.displayName }}
            </option>
          </optgroup>
        </select>
      </div>
      
      <button class="load-api-btn" @click="loadSelectedBlock" :disabled="loading || !selectedBlockId">
        {{ loading ? 'Chargement...' : 'Charger le bloc sélectionné' }}
      </button>
      <button class="reset-btn" @click="resetFlow">Réinitialiser</button>
      <button class="debug-btn" @click="toggleDebugMode">{{ debugMode ? 'Désactiver' : 'Activer' }} le mode debug</button>
      <button class="flow-debug-btn" @click="toggleFlowDebug">{{ showFlowDebug ? 'Masquer' : 'Afficher' }} debug flow</button>
    </div>

    <!-- Section de debug du flow -->
    <div v-if="showFlowDebug" class="flow-debug-section">
      <div class="debug-panel">
        <div class="debug-header">
          <h3>🐛 Debug Flow en temps réel</h3>
          <div class="debug-controls">
            <button @click="clearDebugLogs" class="clear-logs-btn">Vider les logs</button>
            <button @click="exportFlowJson" class="export-btn">Exporter JSON</button>
          </div>
        </div>
        
        <div class="debug-content">
          <!-- Informations générales du flow -->
          <div class="debug-section">
            <h4>📋 Informations du Flow</h4>
            <div class="debug-info-grid">
              <div class="info-item">
                <span class="label">ID:</span>
                <span class="value">{{ flowObject._id || 'N/A' }}</span>
              </div>
              <div class="info-item">
                <span class="label">Type:</span>
                <span class="value">{{ flowObject.type || 'N/A' }}</span>
              </div>
              <div class="info-item">
                <span class="label">Nom du bloc:</span>
                <span class="value">{{ flowObject.blockname || 'N/A' }}</span>
              </div>
              <div class="info-item">
                <span class="label">Nombre d'éléments:</span>
                <span class="value">{{ flowObject.flow ? flowObject.flow.length : 0 }}</span>
              </div>
            </div>
          </div>
          
          <!-- Liste des éléments avec détails -->
          <div class="debug-section">
            <h4>🔧 Éléments du Flow</h4>
            <div class="elements-debug-list">
              <div 
                v-for="(element, index) in flowObject.flow || []" 
                :key="element.id_element || index"
                class="element-debug-item"
                :class="{ 'element-modified': isElementModified(index) }"
              >
                <div class="element-debug-header">
                  <span class="element-index">{{ index + 1 }}</span>
                  <span class="element-type-label">{{ element.type }}</span>
                  <span class="element-id-debug">{{ element.id_element || 'Nouveau' }}</span>
                  <button @click="toggleElementDetails(index)" class="toggle-details-btn">
                    {{ expandedElements.includes(index) ? '▼' : '▶' }}
                  </button>
                </div>
                
                <div v-if="expandedElements.includes(index)" class="element-details">
                  <pre class="element-json">{{ JSON.stringify(element, null, 2) }}</pre>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Logs des changements -->
          <div class="debug-section">
            <h4>📝 Logs des changements</h4>
            <div class="debug-logs" ref="debugLogs">
              <div 
                v-for="(log, index) in debugLogs" 
                :key="index" 
                class="debug-log-item"
                :class="log.type"
              >
                <span class="log-timestamp">{{ formatTime(log.timestamp) }}</span>
                <span class="log-message">{{ log.message }}</span>
                <div v-if="log.data" class="log-data">
                  <pre>{{ JSON.stringify(log.data, null, 2) }}</pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="test-layout">
      <!-- Sidebar des outils -->
      <div class="tools-sidebar">
        <!-- Text Element -->
        <button class="tool-btn" @click="addElement('text')" @mouseenter="showElementInfo('text', $event)" @mouseleave="hideElementInfo" title="Texte - Message simple">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
            <path d="M14 2v6h6"/>
            <path d="M16 13H8"/>
            <path d="M16 17H8"/>
            <path d="M10 9H8"/>
          </svg>
        </button>
        <!-- Text with Buttons Element -->
        <button class="tool-btn" @click="addElement('text_button')" @mouseenter="showElementInfo('text_button', $event)" @mouseleave="hideElementInfo" title="Texte avec boutons - Message avec boutons interactifs">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
            <path d="M9 9h6"/>
            <path d="M9 15h6"/>
            <rect x="7" y="12" width="2" height="2"/>
            <rect x="7" y="16" width="2" height="2"/>
          </svg>
        </button>
        <!-- List Element -->
        <button class="tool-btn" @click="addElement('list')" @mouseenter="showElementInfo('list', $event)" @mouseleave="hideElementInfo" title="Liste interactive - Menu de sélection">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="8" y1="6" x2="21" y2="6"/>
            <line x1="8" y1="12" x2="21" y2="12"/>
            <line x1="8" y1="18" x2="21" y2="18"/>
            <line x1="3" y1="6" x2="3.01" y2="6"/>
            <line x1="3" y1="12" x2="3.01" y2="12"/>
            <line x1="3" y1="18" x2="3.01" y2="18"/>
          </svg>
        </button>
        <!-- Image Element -->
        <button class="tool-btn" @click="addElement('image')" @mouseenter="showElementInfo('image', $event)" @mouseleave="hideElementInfo" title="Image - Contenu visuel">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
            <circle cx="8.5" cy="8.5" r="1.5"/>
            <path d="M21 15l-5-5L5 21"/>
          </svg>
        </button>
        <!-- Audio Element -->
        <button class="tool-btn" @click="addElement('audio')" @mouseenter="showElementInfo('audio', $event)" @mouseleave="hideElementInfo" title="Audio - Fichier audio">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M9 18V5l12-2v13"/>
            <circle cx="6" cy="18" r="3"/>
            <circle cx="18" cy="16" r="3"/>
          </svg>
        </button>
        <!-- Video Element -->
        <button class="tool-btn" @click="addElement('video')" @mouseenter="showElementInfo('video', $event)" @mouseleave="hideElementInfo" title="Vidéo - Fichier vidéo MP4">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polygon points="23 7 16 12 23 17 23 7"></polygon>
            <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
          </svg>
        </button>
        <!-- Delay Element -->
        <button class="tool-btn" @click="addElement('delay')" @mouseenter="showElementInfo('delay', $event)" @mouseleave="hideElementInfo" title="Délai - Pause dans le flux">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M12 6v6l4 2"/>
          </svg>
        </button>
        <!-- Request Element -->
        <button class="tool-btn" @click="addElement('request')" @mouseenter="showElementInfo('request', $event)" @mouseleave="hideElementInfo" title="Requête HTTP - Appel API">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>
            <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
            <ellipse cx="12" cy="5" rx="9" ry="3"/>
          </svg>
        </button>
        <!-- Answer Element -->
        <button class="tool-btn" @click="addElement('answer')" @mouseenter="showElementInfo('answer', $event)" @mouseleave="hideElementInfo" title="Réponse utilisateur - Capture d'input">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
            <path d="M13 8H7"/>
            <path d="M17 12H7"/>
          </svg>
        </button>
        <!-- Redirect Element -->
        <button class="tool-btn" @click="addElement('redirect')" @mouseenter="showElementInfo('redirect', $event)" @mouseleave="hideElementInfo" title="Redirection - Changement de flux">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M5 12h14"/>
            <path d="M12 5l7 7-7 7"/>
          </svg>
        </button>
        <!-- Notification Element -->
        <button class="tool-btn" @click="addElement('notification')" @mouseenter="showElementInfo('notification', $event)" @mouseleave="hideElementInfo" title="Notification - Alerte">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
            <path d="M13.73 21a2 2 0 01-3.46 0"/>
          </svg>
        </button>
        <!-- Variable Insert Element -->
        <button class="tool-btn" @click="addElement('variable_insert')" @mouseenter="showElementInfo('variable_insert', $event)" @mouseleave="hideElementInfo" title="Insertion de variable - Gestion de variables">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M7 21h10"/>
            <path d="M12 21v-8"/>
            <path d="M5 11c6-6 6-6 12 0"/>
            <circle cx="12" cy="7" r="4"/>
          </svg>
        </button>
      </div>

      <!-- Zone de rendu et de test -->
      <div class="test-canvas">
        <div class="flow-info" v-if="flowObject.flow && flowObject.flow.length > 0">
          <h3>{{ flowObject.flow.length }} éléments dans le flow</h3>
          <p>Block: {{ flowObject.blockname }}</p>
          <p v-if="flowObject._id">ID: {{ flowObject._id }}</p>
          <div class="variables-info" v-if="variables.length > 0">
            <p><strong>Variables disponibles:</strong> {{ variables.length }}</p>
            <div class="variables-preview">
              <span v-for="variable in variables.slice(0, 5)" :key="variable.id" class="variable-tag">
                {{ variable.id }}
              </span>
              <span v-if="variables.length > 5" class="more-variables">
                +{{ variables.length - 5 }} autres
              </span>
        </div>
            
            <!-- Debug détaillé des variables -->
            <div class="variables-debug" v-if="debugMode">
              <h5>🐛 Debug Variables</h5>
              <div class="variables-debug-content">
                <div class="debug-stats">
                  <span class="debug-stat">Total: {{ variables.length }}</span>
                  <span class="debug-stat">Globales: {{ variables.filter(v => v.type === 'global').length }}</span>
                  <span class="debug-stat">Chatbot: {{ variables.filter(v => v.type !== 'global').length }}</span>
                </div>
                <div class="variables-list-debug">
                  <div v-for="(variable, index) in variables" :key="variable.id" class="variable-debug-item">
                    <span class="variable-index">{{ index + 1 }}</span>
                    <span class="variable-id">{{ variable.id }}</span>
                    <span class="variable-type" :class="variable.type">{{ variable.type || 'unknown' }}</span>
                    <span class="variable-slug">{{ variable.slug }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-if="loadingVariables" class="variables-loading">
            <p>🔄 Chargement des variables...</p>
          </div>
          <div v-if="variablesError" class="variables-error">
            <p>⚠️ Erreur variables: {{ variablesError }}</p>
          </div>
        </div>
        
        <div v-if="loading" class="loading-state">
          <div class="loading-spinner">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"></circle>
            </svg>
          </div>
          <p>Chargement du flow depuis l'API...</p>
        </div>
        
        <div v-if="error" class="error-state">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M12 8v4M12 16h.01"/>
          </svg>
          <p>{{ error }}</p>
          <button @click="loadSelectedBlock" class="retry-btn">Réessayer</button>
        </div>
        
        <div v-if="!loading && !error && (!flowObject.flow || flowObject.flow.length === 0)" class="empty-flow">
          <p>Aucun élément à afficher. Sélectionnez un bloc dans la liste déroulante et cliquez sur "Charger le bloc sélectionné" pour commencer.</p>
          <div class="api-info">
            <p><strong>Configuration par défaut:</strong></p>
            <p>Chatbot ID: {{ defaultChatbotId }}</p>
            <p v-if="selectedBlockId">Block ID sélectionné: {{ selectedBlockId }}</p>
            <p v-else>Aucun bloc sélectionné</p>
            <p v-if="loadingBlocks">🔄 Chargement de la liste des blocs...</p>
            <p v-else-if="primaryBlocks.length + secondaryBlocks.length > 0">📋 {{ primaryBlocks.length + secondaryBlocks.length }} blocs disponibles</p>
          </div>
        </div>
        
        <div v-if="successMessage" class="success-state">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M20 6L9 17l-5-5"></path>
          </svg>
          <p>{{ successMessage }}</p>
        </div>
          <pre>{{ flowObject.flow }}</pre>
        <!-- Affichage des éléments du flow avec les vrais composants -->
        <div
          v-for="(element, index) in flowObject.flow || []"
          :key="element.id_element || index"
          class="test-element-block"
        >
          <div class="element-header">
            <span class="element-type">{{ getElementTypeLabel(element.type) }}</span>
            <span class="element-id">ID: {{ element.id_element || 'Nouveau' }}</span>
            <div class="element-actions">
              <button class="action-btn delete-btn" @click="removeElement(index)" title="Supprimer">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M3 6h18"/>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                </svg>
              </button>
            </div>
          </div>
          
          <!-- Utilisation du vrai composant correspondant -->
          <component
            :is="getElementComponent(element.type)"
            :element="element"
            :index="index"
            :id-bot="defaultChatbotId"
            :variables="variables"
            @update="updateElement($event, index)"
            @remove="removeElement(index)"
            @duplicate="duplicateElement(index)"
            :class="{ 'debug-highlighted': debugMode }"
          >
            <template #fallback>
              <div class="fallback-element">
                Composant introuvable pour type: {{ element.type }}
              </div>
            </template>
          </component>
          
          <div class="element-footer" v-if="debugMode">
            <pre class="element-debug">{{ JSON.stringify(element, null, 2) }}</pre>
          </div>
          
          <div class="element-navigation">
            <span class="element-previous" v-if="element.id_previous">
              <strong>Précédent:</strong> {{ element.id_previous }}
            </span>
            <span class="element-previous" v-else>
              <strong>Premier élément du flow</strong>
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Panneau d'information dynamique -->
    <div v-if="elementInfo.show" class="element-info-panel">
      <h4>{{ elementInfo.title }}</h4>
      <p>{{ elementInfo.description }}</p>
    </div>
  </div>
</template>

<script>
import { ELEMENT_COMPONENTS } from '@/components/flowelements';
import { axiosPeelo } from '@/utils/axios';
import { consoleLog, consoleError, consoleWarn } from '@/utils/logger';

export default {
  name: 'FlowElementTest',
  data() {
    return {
      // Valeurs par défaut pour les tests
      defaultChatbotId: '64552c2684696334baffc3d7',
      loading: false,
      error: null,
      successMessage: null,
      
      // Variables centralisées pour tous les composants
      variables: [],
      loadingVariables: false,
      variablesError: null,
      
      // Utilisation directe de flowObject.flow au lieu de testElements
      flowObject: {
        "_id": "645a768484696334baffc3e3",
        "type": "standard",
        "idbot": "64552c2684696334baffc3d7",
        "idblock": "investinafrica",
        "flow": [],
        "blockname": "invest in Africa",
        "displayName": "invest in Africa",
        "category": "standard"
      },
      element_template: {
        text: {
          type: "text",
          id_element: null,
          id_previous: null,
          message: "Nouveau message de test",
          preview_url: true
        },
        text_button: {
          type: "text_button",
          id_element: null,
          id_previous: null,
          message: "Nouveau message avec boutons",
          buttons: [
            {
              "id": "idbutton1",
              "title": "Bouton 1"
            },
            {
              "id": "idbutton2",
              "title": "Bouton 2"
              
            }
          ]
        },
        list: {
          type: "list",
          id_element: null,
          id_previous: null,
          interactive: {
            type: "list",
            header: {
              type: "text",
              text: "HEADER_TEXT"
            },
            body: {
              text: "BODY_TEXT"
            },
            footer: {
              text: "FOOTER_TEXT"
            },
            action: {
              button: "BUTTON_TEXT",
              sections: [
                {
                  title: "SECTION_1_TITLE",
                  rows: [
                    {
                      id: "SECTION_1_ROW_1_ID",
                      title: "SECTION_1_ROW_1_TITLE",
                      description: "SECTION_1_ROW_1_DESCRIPTION"
                    }
                  ]
                }
              ]
            }
          }
        },
        image: {
          type: "image",
          id_element: null,
          id_previous: null,
          link: ""
        },
        audio: {
          type: "audio",
          id_element: null,
          id_previous: null,
          link: ""
        },
        delay: {
          type: "delay",
          id_element: null,
          id_previous: null,
          timmer: 2
        },
        request: {
          type: "request",
          id_element: null,
          id_previous: null,
          method: "POST",
          url: "https://api.peelo.io/send/datas",
          datas: ["variablename1", "variablename2"]
        },
        answer: {
          type: "answer",
          id_element: null,
          id_previous: null,
          options: {
            answer_type: ["text"],
            variable: {
              id: "user_answer"
            },
            default_reply: "Désolé je ne comprends pas, veuillez réessayer."
          }
        },
        redirect: {
          type: "redirection",
          id_element: null,
          id_previous: null,
          redirection_block: "block_name"
        },
        notification: {
          type: 'notification',
          to: "2217XXXXXXXX",
          id_element: null,
          id_previous: null,
          notification: {
            type: "text",
            message: "Nouvelle notification",
            preview_url: true
          }
        },
        variable_insert: {
          "type": "variable_insert",
          "id_element": null,
          "id_previous": null,
          "variable": {
            "id": "nouvelle_variable",
            "value": "valeur_par_defaut"
          }
        }
      },
      elementInfo: {
        show: false,
        title: '',
        description: ''
      },
      debugMode: false,
      selectedBlockId: '',
      primaryBlocks: [],
      secondaryBlocks: [],
      loadingBlocks: false,
      showFlowDebug: false,
      expandedElements: [],
      debugLogs: [],
    };
  },
  async mounted() {
    await this.getBlocks();
    
    // Initialiser les logs de debug
    this.addDebugLog({
      type: 'info',
      message: 'FlowElementTest initialisé',
      data: {
        chatbotId: this.defaultChatbotId,
        timestamp: new Date().toISOString()
      }
    });
    
    // Écouter la touche Échap pour fermer le debug
    document.addEventListener('keydown', this.handleKeyDown);
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown);
  },
  methods: {
    async getBlocks() {
      this.loadingBlocks = true;
      try {
        // Récupérer le token d'authentification
        const userData = localStorage.getItem('user');
        if (!userData) {
          throw new Error('Données utilisateur non trouvées. Veuillez vous connecter.');
        }
        
        const userObj = JSON.parse(userData);
        const token = userObj.data.token;
        const config = { headers: { 'Authorization': `Bearer ${token}` } };
        
        const response = await axiosPeelo.get(
          `/strong/get/chatbot/${this.defaultChatbotId}/flow`,
          config
        );
        
        if (response.data && response.data.success) {
          const flowData = response.data.flow;
          
          // Traiter les blocs principaux (inspiré de PlaygroundView.vue)
          this.primaryBlocks = [];
          if (flowData.mainblocks) {
            // Welcome blocks
            if (flowData.mainblocks.welcome) {
              this.primaryBlocks.push(...flowData.mainblocks.welcome.map(block => ({
                ...block,
                category: 'welcome',
                displayName: 'Welcome Flow'
              })));
            }
            
            // Default blocks
            if (flowData.mainblocks.default) {
              this.primaryBlocks.push(...flowData.mainblocks.default.map(block => ({
                ...block,
                category: 'default',
                displayName: 'Default Flow'
              })));
            }
            
            // Commerce blocks
            if (flowData.mainblocks.commerce) {
              this.primaryBlocks.push(...flowData.mainblocks.commerce.map(block => ({
                ...block,
                category: 'commerce',
                displayName: 'Commerce Flow'
              })));
            }
          }
          
          // Traiter les blocs secondaires
          this.secondaryBlocks = flowData.blocks ? flowData.blocks.map(block => ({
            ...block,
            category: 'standard',
            displayName: block.blockname && block.blockname !== null ? block.blockname : block.idblock
          })) : [];
          
          consoleLog('Blocs chargés:', {
            primaryBlocks: this.primaryBlocks.length,
            secondaryBlocks: this.secondaryBlocks.length
          });
        } else {
          throw new Error(response.data.error || 'Erreur lors de la récupération des blocs');
        }
      } catch (error) {
        consoleError('Erreur lors de la récupération des blocs:', error);
        this.error = error.message || 'Impossible de récupérer la liste des blocs';
      } finally {
        this.loadingBlocks = false;
      }
    },
    async loadSelectedBlock() {
      this.loading = true;
      this.error = null;
      
      try {
        // Récupérer le token d'authentification
        const userData = localStorage.getItem('user');
        if (!userData) {
          throw new Error('Données utilisateur non trouvées. Veuillez vous connecter.');
        }
        
        const userObj = JSON.parse(userData);
        const token = userObj.data.token;
        const config = { headers: { 'Authorization': `Bearer ${token}` } };
        
        // Faire la requête à l'API
        const response = await axiosPeelo.get(
          `/strong/get/flow/${this.defaultChatbotId}/${this.selectedBlockId}`, 
          config
        );
        
        if (response.data && response.data.success) {
          // Traiter la structure de réponse de l'API
          if (response.data.block && Array.isArray(response.data.block) && response.data.block.length > 0) {
            // Récupérer le premier élément du tableau block
            const blockData = response.data.block[0];
            
            // Conserver les propriétés displayName et category si elles existent
            blockData.displayName = blockData.displayName || blockData.blockname || 'Flow chargé depuis API';
            blockData.category = blockData.category || 'API';
            
            // Mettre à jour le flowObject avec les données complètes
            this.flowObject = blockData;
            consoleLog('Flow chargé depuis l\'API:', this.flowObject);
            
            // Récupérer les variables après avoir chargé le flow
            await this.fetchVariables();
            
            // Afficher une notification de succès
            this.showSuccessMessage(`Flow "${blockData.blockname || blockData.displayName}" chargé avec succès (${blockData.flow?.length || 0} éléments)`);
          } else {
            throw new Error('Structure de données inattendue dans la réponse');
          }
        } else {
          throw new Error(response.data.error || 'Erreur lors de la récupération du flow');
        }
      } catch (error) {
        consoleError('Erreur lors du chargement du flow:', error);
        this.error = error.message || 'Impossible de charger le flow depuis l\'API';
      } finally {
        this.loading = false;
      }
    },
    
    async fetchVariables() {
      this.loadingVariables = true;
      this.variablesError = null;
      
      try {
        // Variables globales à ajouter en haut de la liste
        const globalVariables = [
          { id: 'first_name', type: 'global', slug: 'first_name' },
          { id: 'last_name', type: 'global', slug: 'last_name' },
          { id: 'reply_phone', type: 'global', slug: 'reply_phone' },
          { id: 'full_name', type: 'global', slug: 'full_name' },
          { id: 'phone_number', type: 'global', slug: 'phone_number' },
          { id: 'last_input', type: 'global', slug: 'last_input' },
          { id: 'id_user', type: 'global', slug: 'id_user' },
          { id: 'phone_number_id', type: 'global', slug: 'phone_number_id' },
          { id: 'bot_number', type: 'global', slug: 'bot_number' }
        ];
        
        // Récupérer le token d'authentification
        const userData = localStorage.getItem('user');
        if (!userData) {
          throw new Error('Données utilisateur non trouvées');
        }
        
        const userObj = JSON.parse(userData);
        const token = userObj.data.token;
        const config = {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        };
        
        consoleLog('Récupération des variables pour le chatbot ID:', this.defaultChatbotId);
        
        let chatbotVariables = [];
        
        try {
          const response = await axiosPeelo.get(
            `/get/strong/variables/${this.defaultChatbotId}`,
            config
          );
          
          consoleLog('🔍 Réponse complète de l\'API:', response);
          consoleLog('🔍 response.data:', response.data);
          
          // Logique plus flexible pour différentes structures de réponse
          if (response.data) {
            let responseVariables = null;
            
            // Cas 1: response.data.success && response.data.variables (structure avec success)
            if (response.data.success && response.data.variables) {
              responseVariables = response.data.variables;
              consoleLog('✅ Structure avec success détectée');
            }
            // Cas 2: response.data.variables directement (structure simple)
            else if (response.data.variables) {
              responseVariables = response.data.variables;
              consoleLog('✅ Structure simple détectée');
            }
            // Cas 3: response.data est directement un array de variables
            else if (Array.isArray(response.data)) {
              responseVariables = response.data;
              consoleLog('✅ Array direct détecté');
            }
            
            if (responseVariables && Array.isArray(responseVariables)) {
              chatbotVariables = responseVariables;
              consoleLog('✅ Variables du chatbot récupérées de l\'API:', chatbotVariables);
              consoleLog('📊 Nombre de variables du chatbot:', chatbotVariables.length);
              consoleLog('🔍 Première variable du chatbot (exemple):', chatbotVariables[0]);
            } else {
              consoleLog('⚠️ Variables non trouvées ou format incorrect');
              consoleLog('🔍 Type de responseVariables:', typeof responseVariables);
              consoleLog('🔍 responseVariables est array:', Array.isArray(responseVariables));
              consoleLog('🔍 responseVariables:', responseVariables);
              chatbotVariables = [];
            }
          } else {
            consoleLog('⚠️ response.data est vide ou undefined');
            chatbotVariables = [];
          }
        } catch (e) {
          consoleError('❌ Erreur lors de la récupération des variables:', e);
          consoleLog('📄 Détails de l\'erreur:', {
            message: e.message,
            status: e.response?.status,
            statusText: e.response?.statusText,
            data: e.response?.data
          });
          chatbotVariables = [];
        }
        
        // Combiner les variables globales (en haut) avec les variables du chatbot
        this.variables = [...globalVariables, ...chatbotVariables];
        
        consoleLog('🎯 Variables finales combinées:', {
          'variables globales': globalVariables.length,
          'variables chatbot': chatbotVariables.length,
          'total': this.variables.length,
          'liste complète': this.variables.map(v => ({ id: v.id, type: v.type }))
        });
        
      } catch (e) {
        consoleError('Erreur globale lors de la récupération des variables:', e);
        this.variablesError = e.message || 'Erreur lors de la récupération des variables';
        
        // En cas d'erreur, utiliser seulement les variables globales
        const globalVariables = [
          { id: 'first_name', type: 'global', slug: 'first_name' },
          { id: 'last_name', type: 'global', slug: 'last_name' },
          { id: 'reply_phone', type: 'global', slug: 'reply_phone' },
          { id: 'full_name', type: 'global', slug: 'full_name' },
          { id: 'phone_number', type: 'global', slug: 'phone_number' },
          { id: 'last_input', type: 'global', slug: 'last_input' },
          { id: 'id_user', type: 'global', slug: 'id_user' },
          { id: 'phone_number_id', type: 'global', slug: 'phone_number_id' },
          { id: 'bot_number', type: 'global', slug: 'bot_number' }
        ];
        
        this.variables = globalVariables;
      } finally {
        this.loadingVariables = false;
      }
    },
    
    showSuccessMessage(message) {
      // Simple notification de succès (peut être améliorée avec un vrai système de notifications)
      consoleLog('✅ Succès:', message);
      // On peut ajouter une propriété successMessage pour l'afficher dans l'UI
      this.successMessage = message;
      setTimeout(() => {
        this.successMessage = null;
      }, 5000);
    },
    
    resetFlow() {
      // Réinitialiser le flow
      this.flowObject.flow = [];
    },
    
    toggleDebugMode() {
      this.debugMode = !this.debugMode;
    },
    
    toggleFlowDebug() {
      this.showFlowDebug = !this.showFlowDebug;
    },
    
    getElementComponent(type) {
      // Vérifier si le type existe directement dans ELEMENT_COMPONENTS
      if (ELEMENT_COMPONENTS[type]) {
        return ELEMENT_COMPONENTS[type];
      }
      
      // Si le type n'est pas trouvé, afficher un message de débogage
      consoleWarn(`Type de composant non trouvé: ${type}. Types disponibles: ${Object.keys(ELEMENT_COMPONENTS).join(', ')}`);
      
      // Essayer de trouver un type similaire
      const normalizedType = type.toLowerCase().replace(/s$/, '');
      
      for (const [key, component] of Object.entries(ELEMENT_COMPONENTS)) {
        if (key.includes(normalizedType) || normalizedType.includes(key)) {
          console.info(`Utilisation du composant ${key} pour le type ${type}`);
          return component;
        }
      }
      
      return null;
    },
    
    getElementTypeLabel(type) {
      const labels = {
        text: 'Message Texte',
        text_button: 'Message avec Boutons',
        list: 'Liste Interactive',
        image: 'Image',
        audio: 'Audio',
        delay: 'Délai',
        request: 'Requête HTTP',
        answer: 'Réponse Utilisateur',
        redirection: 'Redirection',
        redirect: 'Redirection',
        notification: 'Notification',
        variable_insert: 'Insertion Variable'
      };
      return labels[type] || type.toUpperCase();
    },
    
    addElement(elementType) {
      const newElement = JSON.parse(JSON.stringify(this.element_template[elementType]));
      newElement.id_element = 'temp_' + Date.now();
      
      // Ajouter l'élément
      this.flowObject.flow.push(newElement);
      
      // Loguer l'ajout
      this.addDebugLog({
        type: 'add',
        message: `Nouvel élément ${elementType} ajouté à la position ${this.flowObject.flow.length}`,
        data: {
          element: newElement,
          type: elementType,
          position: this.flowObject.flow.length - 1
        }
      });
    },
    
    removeElement(index) {
      if (index >= 0 && index < this.flowObject.flow.length) {
        const removedElement = this.flowObject.flow[index];
        
        // Supprimer l'élément
        this.flowObject.flow.splice(index, 1);
        
        // Loguer la suppression
        this.addDebugLog({
          type: 'remove',
          message: `Élément ${index + 1} (${removedElement.type}) supprimé`,
          data: {
            element: removedElement,
            index: index,
            nouveauNombreElements: this.flowObject.flow.length
          }
        });
      }
    },
    
    duplicateElement(index) {
      if (index >= 0 && index < this.flowObject.flow.length) {
        const originalElement = this.flowObject.flow[index];
        const duplicatedElement = JSON.parse(JSON.stringify(originalElement));
        duplicatedElement.id_element = 'temp_' + Date.now();
        
        // Insérer la copie après l'original
        this.flowObject.flow.splice(index + 1, 0, duplicatedElement);
        
        // Loguer la duplication
        this.addDebugLog({
          type: 'duplicate',
          message: `Élément ${index + 1} (${originalElement.type}) dupliqué à la position ${index + 2}`,
          data: {
            original: originalElement,
            copie: duplicatedElement,
            positionOriginale: index,
            positionCopie: index + 1
          }
        });
      }
    },
    
    onEdit(idx, newData) {
      // Filtrer les propriétés inutiles de l'événement
      const { isTrusted, _vts, ...cleanData } = newData;
      
      // Mettre à jour l'élément avec les données nettoyées
      this.flowObject.flow[idx] = { ...this.flowObject.flow[idx], ...cleanData };
    },
    
    genId() {
      return 'el_' + Math.random().toString(36).slice(2, 10) + '_' + Date.now();
    },
    
    showElementInfo(type, event) {
      const infoMap = {
        text: { title: 'Message Texte', description: 'Affiche un message simple à l\'utilisateur' },
        text_button: { title: 'Message avec Boutons', description: 'Message avec boutons interactifs pour les réponses' },
        list: { title: 'Liste Interactive', description: 'Menu de sélection avec options multiples' },
        image: { title: 'Image', description: 'Affiche une image dans la conversation' },
        audio: { title: 'Audio', description: 'Lit un fichier audio' },
        delay: { title: 'Délai', description: 'Pause dans l\'exécution du flux' },
        request: { title: 'Requête HTTP', description: 'Effectue un appel API externe' },
        answer: { title: 'Réponse Utilisateur', description: 'Capture et stocke la réponse de l\'utilisateur' },
        redirect: { title: 'Redirection', description: 'Redirige vers un autre bloc de conversation' },
        notification: { title: 'Notification', description: 'Envoie une notification' },
        variable_insert: { title: 'Insertion Variable', description: 'Insère ou modifie une variable' }
      };
      
      this.elementInfo = {
        show: true,
        title: infoMap[type]?.title || type,
        description: infoMap[type]?.description || 'Description non disponible',
      };
    },
    
    hideElementInfo() {
      this.elementInfo = { show: false, title: '', description: '' };
    },
    
    isElementModified(index) {
      const originalElement = this.flowObject.flow[index];
      const modifiedElement = this.flowObject.flow[index];
      return JSON.stringify(originalElement) !== JSON.stringify(modifiedElement);
    },
    
    toggleElementDetails(index) {
      if (this.expandedElements.includes(index)) {
        this.expandedElements = this.expandedElements.filter(i => i !== index);
      } else {
        this.expandedElements.push(index);
      }
    },
    
    clearDebugLogs() {
      this.debugLogs = [];
    },
    
    exportFlowJson() {
      const json = JSON.stringify(this.flowObject, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${this.flowObject.blockname || 'flow'}_${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      URL.revokeObjectURL(link.href);
    },
    
    formatTime(timestamp) {
      const date = new Date(timestamp);
      return date.toLocaleTimeString();
    },
    
    updateElement(updatedElement, index) {
      // Sauvegarder l'ancien élément pour comparaison
      const oldElement = JSON.parse(JSON.stringify(this.flowObject.flow[index]));
      
      // Mettre à jour l'élément
      this.flowObject.flow[index] = { ...this.flowObject.flow[index], ...updatedElement };
      
      // Loguer le changement
      this.addDebugLog({
        type: 'update',
        message: `Élément ${index + 1} (${oldElement.type}) modifié`,
        data: {
          avant: oldElement,
          après: this.flowObject.flow[index],
          changements: this.getElementChanges(oldElement, this.flowObject.flow[index])
        }
      });
      
      // Forcer la réactivité
      this.$forceUpdate();
    },
    
    getElementChanges(oldElement, newElement) {
      const changes = {};
      const allKeys = new Set([...Object.keys(oldElement), ...Object.keys(newElement)]);
      
      for (const key of allKeys) {
        const oldValue = oldElement[key];
        const newValue = newElement[key];
        
        if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
          changes[key] = {
            ancien: oldValue,
            nouveau: newValue
          };
        }
      }
      
      return changes;
    },
    
    addDebugLog(logEntry) {
      const log = {
        timestamp: Date.now(),
        ...logEntry
      };
      
      this.debugLogs.unshift(log);
      
      // Limiter à 50 logs maximum
      if (this.debugLogs.length > 50) {
        this.debugLogs = this.debugLogs.slice(0, 50);
      }
      
      // Auto-scroll vers le bas des logs
      this.$nextTick(() => {
        if (this.$refs.debugLogs) {
          this.$refs.debugLogs.scrollTop = 0;
        }
      });
      
      // Console log pour debugging
      consoleLog(`[FlowElementTest] ${log.message}`, log.data);
    },
    
    handleKeyDown(event) {
      if (event.key === 'Escape' && this.showFlowDebug) {
        this.showFlowDebug = false;
      }
    },
  },
};
</script>

<style scoped>
.flow-element-test-container {
  padding: 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  background-color: #f8fafc;
  min-height: 100vh;
}

.flow-element-test-container h2 {
  margin-bottom: 20px;
  color: #1f2937;
  font-weight: 600;
  font-size: 1.5rem;
}

.flow-controls {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.block-selector {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-right: 16px;
}

.block-selector label {
  font-weight: 500;
  color: #4b5563;
  font-size: 0.875rem;
  white-space: nowrap;
}

.block-select {
  padding: 10px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  color: #374151;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.9), rgba(249, 250, 251, 0.8));
  min-width: 250px;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

.block-select:focus {
  border-color: #25D366;
  outline: none;
  box-shadow: 0 0 0 3px rgba(37, 211, 102, 0.1);
}

.block-select:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: rgba(243, 244, 246, 0.8);
}

.block-select optgroup {
  font-weight: 600;
  color: #374151;
  background: rgba(249, 250, 251, 0.9);
}

.block-select option {
  padding: 8px;
  color: #6b7280;
}

.load-flow-btn, .load-api-btn, .reset-btn, .debug-btn, .flow-debug-btn {
  padding: 10px 16px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  font-size: 0.875rem;
}

.load-flow-btn {
  background-color: #25D366;
  color: white;
  box-shadow: 0 2px 4px rgba(37, 211, 102, 0.2);
}

.load-flow-btn:hover {
  background-color: #1fb959;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(37, 211, 102, 0.3);
}

.load-api-btn {
  background-color: #3b82f6;
  color: white;
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.2);
}

.load-api-btn:hover {
  background-color: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3);
}

.reset-btn {
  background-color: #6b7280;
  color: white;
  box-shadow: 0 2px 4px rgba(107, 114, 128, 0.2);
}

.reset-btn:hover {
  background-color: #4b5563;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(107, 114, 128, 0.3);
}

.debug-btn {
  background-color: #3b82f6;
  color: white;
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.2);
}

.debug-btn:hover {
  background-color: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3);
}

.flow-debug-btn {
  background-color: #3b82f6;
  color: white;
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.2);
}

.flow-debug-btn:hover {
  background-color: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3);
}

.test-layout {
  display: flex;
  gap: 24px;
  height: calc(100vh - 140px);
}

.tools-sidebar {
  width: 60px;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.9), rgba(249, 250, 251, 0.8));
  border-radius: 12px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.tool-btn {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.8), rgba(249, 250, 251, 0.7));
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.tool-btn:hover {
  background: linear-gradient(145deg, #25D366, #20bd5c);
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(37, 211, 102, 0.2);
}

.test-canvas {
  flex: 1;
  padding: 24px;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.9), rgba(249, 250, 251, 0.8));
  border-radius: 12px;
  overflow-y: auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.flow-info {
  margin-bottom: 24px;
  padding: 16px;
  background: linear-gradient(145deg, rgba(37, 211, 102, 0.1), rgba(37, 211, 102, 0.05));
  border-radius: 8px;
  border-left: 4px solid #25D366;
  box-shadow: 0 2px 4px rgba(37, 211, 102, 0.1);
}

.flow-info h3 {
  margin: 0 0 8px 0;
  color: #25D366;
  font-size: 1.1rem;
  font-weight: 600;
}

.flow-info p {
  margin: 0;
  color: #4b5563;
  font-size: 0.875rem;
}

.variables-info {
  margin-top: 12px;
  padding: 12px;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.8), rgba(249, 250, 251, 0.6));
  border-radius: 8px;
  border: 1px solid rgba(37, 211, 102, 0.2);
}

.variables-preview {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.variable-tag {
  background: linear-gradient(145deg, rgba(37, 211, 102, 0.15), rgba(37, 211, 102, 0.1));
  color: #059669;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  border: 1px solid rgba(37, 211, 102, 0.2);
}

.more-variables {
  background: linear-gradient(145deg, rgba(107, 114, 128, 0.15), rgba(107, 114, 128, 0.1));
  color: #6b7280;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  border: 1px solid rgba(107, 114, 128, 0.2);
}

.variables-loading {
  margin-top: 8px;
  color: #3b82f6;
  font-size: 0.875rem;
}

.variables-error {
  margin-top: 8px;
  color: #ef4444;
  font-size: 0.875rem;
}

.loading-state {
  padding: 40px;
  text-align: center;
  background: linear-gradient(145deg, rgba(243, 244, 246, 0.8), rgba(229, 231, 235, 0.6));
  border-radius: 12px;
  color: #6b7280;
  font-style: italic;
  border: 2px dashed rgba(156, 163, 175, 0.3);
}

.loading-spinner {
  margin-bottom: 16px;
}

.error-state {
  padding: 40px;
  text-align: center;
  background: linear-gradient(145deg, rgba(254, 242, 242, 0.8), rgba(254, 226, 226, 0.6));
  border-radius: 12px;
  color: #b91c1c;
  font-weight: 500;
  border: 2px dashed rgba(239, 68, 68, 0.3);
}

.retry-btn {
  padding: 10px 16px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  font-size: 0.875rem;
  background-color: #25D366;
  color: white;
  box-shadow: 0 2px 4px rgba(37, 211, 102, 0.2);
}

.retry-btn:hover {
  background-color: #1fb959;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(37, 211, 102, 0.3);
}

.empty-flow {
  padding: 40px;
  text-align: center;
  background: linear-gradient(145deg, rgba(243, 244, 246, 0.8), rgba(229, 231, 235, 0.6));
  border-radius: 12px;
  color: #6b7280;
  font-style: italic;
  border: 2px dashed rgba(156, 163, 175, 0.3);
}

.api-info {
  margin-top: 20px;
  padding: 16px;
  background: linear-gradient(145deg, rgba(243, 244, 246, 0.8), rgba(229, 231, 235, 0.6));
  border-radius: 12px;
  color: #6b7280;
  font-size: 0.875rem;
}

.test-element-block {
  margin-bottom: 20px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.95), rgba(249, 250, 251, 0.9));
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  transition: all 0.2s ease;
}

.test-element-block:hover {
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

.element-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: linear-gradient(145deg, rgba(249, 250, 251, 0.8), rgba(243, 244, 246, 0.6));
  border-bottom: 1px solid rgba(229, 231, 235, 0.5);
}

.element-type {
  font-weight: 600;
  color: #25D366;
  font-size: 0.875rem;
}

.element-id {
  font-size: 0.75rem;
  color: #9ca3af;
}

.element-actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.9), rgba(249, 250, 251, 0.8));
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.action-btn.delete-btn:hover {
  background: linear-gradient(145deg, rgba(239, 68, 68, 0.15), rgba(239, 68, 68, 0.05));
  color: #ef4444;
  transform: scale(1.1);
  box-shadow: 0 4px 8px rgba(239, 68, 68, 0.2);
}

.element-navigation {
  padding: 12px 16px;
  background: linear-gradient(145deg, rgba(249, 250, 251, 0.6), rgba(243, 244, 246, 0.4));
  border-top: 1px solid rgba(229, 231, 235, 0.3);
  font-size: 0.8rem;
  color: #6b7280;
}

.element-debug {
  background: linear-gradient(145deg, rgba(243, 244, 246, 0.8), rgba(229, 231, 235, 0.6));
  border-radius: 6px;
  padding: 12px;
  font-size: 0.75rem;
  margin: 12px 16px;
  color: #4b5563;
  white-space: pre-wrap;
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid rgba(209, 213, 219, 0.3);
}

.fallback-element {
  padding: 20px;
  background: linear-gradient(145deg, rgba(254, 242, 242, 0.8), rgba(254, 226, 226, 0.6));
  color: #b91c1c;
  font-weight: 500;
  border-radius: 8px;
  margin: 16px;
  text-align: center;
  border: 1px solid rgba(239, 68, 68, 0.2);
}

.element-info-panel {
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.95), rgba(249, 250, 251, 0.9));
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  max-width: 300px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  animation: slideInUp 0.3s ease;
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.element-info-panel h4 {
  margin: 0 0 8px 0;
  color: #25D366;
  font-size: 1rem;
  font-weight: 600;
}

.element-info-panel p {
  margin: 0;
  color: #4b5563;
  font-size: 0.875rem;
  line-height: 1.4;
}

.success-state {
  padding: 20px;
  text-align: center;
  background: linear-gradient(145deg, rgba(236, 253, 245, 0.8), rgba(220, 252, 231, 0.6));
  border-radius: 12px;
  color: #059669;
  font-weight: 500;
  border: 2px solid rgba(16, 185, 129, 0.3);
  margin-bottom: 20px;
  animation: slideInDown 0.3s ease;
}

.success-state svg {
  margin-bottom: 8px;
  color: #10b981;
}

@keyframes slideInDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .test-layout {
    flex-direction: column;
    height: auto;
  }
  
  .tools-sidebar {
    width: 100%;
    flex-direction: row;
    overflow-x: auto;
  }
  
  .flow-element-test-container {
    padding: 16px;
  }
  
  .flow-controls {
    flex-direction: column;
    gap: 8px;
  }
}

.flow-debug-section {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  top: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 20px;
  z-index: 1000;
  overflow-y: auto;
}

.debug-panel {
  background: white;
  border-radius: 12px;
  padding: 24px;
  width: 90%;
  max-width: 1000px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.debug-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid #f3f4f6;
}

.debug-header h3 {
  margin: 0;
  color: #111827;
  font-size: 1.5rem;
  font-weight: 700;
}

.debug-controls {
  display: flex;
  gap: 12px;
}

.clear-logs-btn, .export-btn {
  padding: 10px 16px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  font-size: 0.875rem;
}

.clear-logs-btn {
  background: linear-gradient(145deg, #fef2f2, #fee2e2);
  color: #dc2626;
  border: 1px solid #fecaca;
}

.clear-logs-btn:hover {
  background: linear-gradient(145deg, #fee2e2, #fecaca);
  transform: translateY(-1px);
}

.export-btn {
  background: linear-gradient(145deg, #f0f9ff, #dbeafe);
  color: #2563eb;
  border: 1px solid #bfdbfe;
}

.export-btn:hover {
  background: linear-gradient(145deg, #dbeafe, #bfdbfe);
  transform: translateY(-1px);
}

.debug-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

@media (max-width: 768px) {
  .debug-content {
    grid-template-columns: 1fr;
  }
}

.debug-section {
  background: #f9fafb;
  border-radius: 8px;
  padding: 16px;
  border: 1px solid #e5e7eb;
}

.debug-section h4 {
  margin: 0 0 16px 0;
  color: #374151;
  font-size: 1.1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
}

.debug-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.info-item {
  background: white;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.label {
  font-weight: 600;
  color: #4b5563;
  font-size: 0.875rem;
}

.value {
  color: #25D366;
  font-weight: 500;
  font-size: 0.875rem;
}

.elements-debug-list {
  max-height: 300px;
  overflow-y: auto;
  margin-bottom: 8px;
}

.element-debug-item {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  margin-bottom: 8px;
  overflow: hidden;
  transition: all 0.2s ease;
}

.element-debug-item:hover {
  border-color: #25D366;
  box-shadow: 0 2px 4px rgba(37, 211, 102, 0.1);
}

.element-modified {
  border-color: #f59e0b;
  background: #fffbeb;
}

.element-debug-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f9fafb;
  cursor: pointer;
}

.element-index {
  background: #25D366;
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.75rem;
  min-width: 24px;
  text-align: center;
}

.element-type-label {
  background: linear-gradient(145deg, #f0f9ff, #dbeafe);
  color: #2563eb;
  padding: 6px 12px;
  border-radius: 16px;
  font-weight: 600;
  font-size: 0.8rem;
  border: 1px solid #bfdbfe;
}

.element-id-debug {
  color: #6b7280;
  font-family: monospace;
  font-size: 0.8rem;
  background: #f3f4f6;
  padding: 4px 8px;
  border-radius: 4px;
}

.toggle-details-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  color: #6b7280;
  font-size: 1rem;
  transition: all 0.2s ease;
}

.toggle-details-btn:hover {
  color: #25D366;
  transform: scale(1.1);
}

.element-details {
  padding: 16px;
  background: white;
  border-top: 1px solid #e5e7eb;
}

.element-json {
  background: #1f2937;
  color: #e5e7eb;
  padding: 12px;
  border-radius: 6px;
  font-size: 0.8rem;
  overflow-x: auto;
  margin: 0;
  line-height: 1.4;
}

.debug-logs {
  max-height: 400px;
  overflow-y: auto;
  background: white;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
}

.debug-log-item {
  padding: 12px 16px;
  border-bottom: 1px solid #f3f4f6;
  transition: all 0.2s ease;
}

.debug-log-item:hover {
  background: #f9fafb;
}

.debug-log-item:last-child {
  border-bottom: none;
}

.debug-log-item.add {
  border-left: 4px solid #10b981;
}

.debug-log-item.update {
  border-left: 4px solid #f59e0b;
}

.debug-log-item.remove {
  border-left: 4px solid #ef4444;
}

.debug-log-item.duplicate {
  border-left: 4px solid #8b5cf6;
}

.log-timestamp {
  font-weight: 600;
  color: #25D366;
  font-size: 0.8rem;
  margin-right: 8px;
}

.log-message {
  color: #374151;
  font-size: 0.875rem;
  font-weight: 500;
}

.log-data {
  margin-top: 8px;
  background: #f9fafb;
  border-radius: 4px;
  border: 1px solid #e5e7eb;
}

.log-data pre {
  padding: 8px;
  margin: 0;
  font-size: 0.75rem;
  color: #4b5563;
  overflow-x: auto;
}

/* Debug Variables Styles */
.variables-debug {
  margin-top: 16px;
  padding: 12px;
  background: linear-gradient(145deg, rgba(59, 130, 246, 0.08), rgba(59, 130, 246, 0.04));
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 8px;
}

.variables-debug h5 {
  margin: 0 0 12px 0;
  color: #2563eb;
  font-size: 0.9rem;
  font-weight: 600;
}

.debug-stats {
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.debug-stat {
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.8), rgba(249, 250, 251, 0.6));
  color: #374151;
  padding: 6px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  border: 1px solid rgba(209, 213, 219, 0.3);
}

.variables-list-debug {
  max-height: 200px;
  overflow-y: auto;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 6px;
  border: 1px solid rgba(209, 213, 219, 0.3);
}

.variable-debug-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-bottom: 1px solid rgba(229, 231, 235, 0.3);
  transition: background-color 0.2s ease;
}

.variable-debug-item:hover {
  background: rgba(249, 250, 251, 0.8);
}

.variable-debug-item:last-child {
  border-bottom: none;
}

.variable-index {
  background: #374151;
  color: white;
  padding: 2px 6px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.7rem;
  min-width: 20px;
  text-align: center;
}

.variable-id {
  font-weight: 600;
  color: #111827;
  font-size: 0.8rem;
  min-width: 100px;
  flex-shrink: 0;
}

.variable-type {
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 500;
  text-transform: uppercase;
}

.variable-type.global {
  background: linear-gradient(145deg, rgba(16, 185, 129, 0.15), rgba(16, 185, 129, 0.1));
  color: #059669;
  border: 1px solid rgba(16, 185, 129, 0.2);
}

.variable-type:not(.global) {
  background: linear-gradient(145deg, rgba(59, 130, 246, 0.15), rgba(59, 130, 246, 0.1));
  color: #2563eb;
  border: 1px solid rgba(59, 130, 246, 0.2);
}

.variable-slug {
  color: #6b7280;
  font-family: monospace;
  font-size: 0.75rem;
  flex: 1;
}
</style>
